import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Upload,
  Button,
  Row,
  Col,
  Typography,
  Modal,
  Space,
  Descriptions,
  message,
} from "antd";
import {
  UploadOutlined,
  SaveOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useParams, useHistory } from "react-router-dom";
import axiosInstance from "../../../configs";

const { Title, Text } = Typography;
const { Option } = Select;

// ==================== SERVICE API ====================

// Ambil list kategori item
const fetchItemCategories = () => axiosInstance.get("/item-category");

// Generate kode barang otomatis (kalau ada di BE)
const generateItemCode = (categoryId) =>
  axiosInstance.get("/item/generate-code", {
    params: { id_item_category: categoryId },
  });

// Create item
const createItem = (payload) => axiosInstance.post("/item", payload);

// Get detail item (untuk edit)
const getItemDetail = (id) => axiosInstance.get(`/item/${id}`);

// Update item
const updateItem = (id, payload) => axiosInstance.put(`/item/${id}`, payload);

// Delete item (kalau nanti mau dipakai di halaman lain)
const deleteItem = (id) => axiosInstance.delete(`/item/${id}`);

// Upload file (foto) – SESUAIKAN endpoint
const uploadFile = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return axiosInstance.post("/uploads/item-photo", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// ==================== KOMPONEN FORM ====================

export default function AddItem() {
  const [form] = Form.useForm();
  const { idItem } = useParams(); // /item/add atau /item/edit/:id
  const history = useHistory();

  const [categories, setCategories] = useState([]);
  const [loadingPage, setLoadingPage] = useState(true);
  const [saving, setSaving] = useState(false);

  const [fileListFoto1, setFileListFoto1] = useState([]);
  const [fileListFoto2, setFileListFoto2] = useState([]);

  const [confirmVisible, setConfirmVisible] = useState(false);
  const [summaryValues, setSummaryValues] = useState(null);

  // watch kategori dari form
  const selectedCategoryId = Form.useWatch("id_item_category", form);

  // Daftar kategori yang HARUS sembunyikan kadar alkohol
  const hideAlcoholCategoryCodes = useMemo(
    () => ["REL", "BARANG PROMOSI", "INVENTARIS"],
    []
  );

  const selectedCategory = useMemo(
    () =>
      categories.find(
        (cat) => String(cat.id_item_category) === String(selectedCategoryId)
      ),
    [categories, selectedCategoryId]
  );

  const shouldHideAlcoholField = useMemo(() => {
    if (!selectedCategory) return false;
    const name = (selectedCategory.category || "").toUpperCase();
    return hideAlcoholCategoryCodes.includes(name);
  }, [selectedCategory, hideAlcoholCategoryCodes]);

  // Kalau kategori termasuk REL / Promo / Inventaris -> set otomatis kadar_alkohol = 0
  useEffect(() => {
    if (shouldHideAlcoholField) {
      form.setFieldsValue({ kadar_alkohol: 0 });
    }
  }, [shouldHideAlcoholField, form]);

  // Load kategori + detail item (kalau edit)
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const catRes = await fetchItemCategories();
        setCategories(catRes.data?.data || catRes.data || []);

        if (idItem) {
          const detailRes = await getItemDetail(idItem);
          const item = detailRes.data?.data || detailRes.data;

          form.setFieldsValue({
            id_item_category: item.id_item_category,
            item_code: item.item_code,
            hs_code: item.hs_code,
            uraian: item.uraian || item.item_name,
            isi_kemasan: Number(item.isi_kemasan),
            isi_per_unit: Number(item.isi_per_unit),
            satuan_kemasan: item.satuan_kemasan,
            satuan_unit: item.satuan_per_unit,
            merk: item.merk,
            kadar_alkohol: item.kadar_alkohol,
            lampiran_foto_1: item.lampiran_1,
            lampiran_foto_2: item.lampiran_2,
          });

          if (item.lampiran_1) {
            setFileListFoto1([
              {
                uid: "-1",
                name: "foto1",
                status: "done",
                url: item.lampiran_1,
              },
            ]);
          }
          if (item.lampiran_2) {
            setFileListFoto2([
              {
                uid: "-2",
                name: "foto2",
                status: "done",
                url: item.lampiran_2,
              },
            ]);
          }
        }
      } catch (err) {
        console.error(err);
        message.error("Gagal memuat data awal");
      } finally {
        setLoadingPage(false);
      }
    };

    loadInitialData();
  }, [idItem, form]);

  // Generate kode barang otomatis ketika kategori dipilih (hanya untuk ADD)
  const handleCategoryChange = async (value) => {
    form.setFieldsValue({ id_item_category: value });

    if (!idItem) {
      try {
        const res = await generateItemCode(value);
        const code = res.data?.data?.item_code || res.data?.item_code;
        if (code) {
          form.setFieldsValue({ item_code: code });
        }
      } catch (err) {
        console.error(err);
        message.warning("Tidak dapat generate kode barang otomatis");
      }
    }
  };

  // Handle upload foto (metode custom upload ke backend)
  const handleCustomUpload = async ({ file, onSuccess, onError }) => {
    try {
      const res = await uploadFile(file);
      const filePath = res.data?.data?.file_path || res.data?.file_path;
      onSuccess(res, file);
      return filePath;
    } catch (err) {
      console.error(err);
      onError(err);
    }
  };

  const onChangeUploadFoto1 = ({ fileList }) => {
    setFileListFoto1(fileList);
  };

  const onChangeUploadFoto2 = ({ fileList }) => {
    setFileListFoto2(fileList);
  };

  const extractUploadedPath = (fileList) => {
    if (!fileList || fileList.length === 0) return null;
    const file = fileList[0];
    if (file.response) {
      return (
        file.response?.data?.file_path ||
        file.response?.file_path ||
        file.response?.url
      );
    }
    return file.url || file.path || null;
  };

  // Submit form -> munculkan modal konfirmasi
  const handleFinish = (values) => {
    const kategoriLabel =
      categories.find(
        (c) => String(c.id_item_category) === String(values.id_item_category)
      )?.category || "";

    const lampiranFoto1 = extractUploadedPath(fileListFoto1);
    const lampiranFoto2 = extractUploadedPath(fileListFoto2);

    const fullValues = {
      ...values,
      kategori_label: kategoriLabel,
      lampiran_foto_1: lampiranFoto1,
      lampiran_foto_2: lampiranFoto2,
    };

    setSummaryValues(fullValues);
    setConfirmVisible(true);
  };

  // Konfirmasi simpan di modal
  const handleConfirmSave = async () => {
    if (!summaryValues) return;

    setSaving(true);
    try {
      const payload = {
        id_item_category: summaryValues.id_item_category ?? null,
        id_usage_unit: null,
        id_item_class: null,
        id_currency: null,
        id_contact: 0, // 0 = Good

        item_code: summaryValues.item_code ?? null,
        item_name: summaryValues.uraian ?? null,
        merk: summaryValues.merk ?? null,

        isi_kemasan:
          summaryValues.isi_kemasan !== undefined
            ? summaryValues.isi_kemasan
            : null,
        satuan_kemasan: summaryValues.satuan_kemasan ?? null,
        isi_per_unit:
          summaryValues.isi_per_unit !== undefined
            ? summaryValues.isi_per_unit
            : null,
        satuan_per_unit: summaryValues.satuan_unit ?? null,

        kadar_alkohol: shouldHideAlcoholField
          ? 0
          : summaryValues.kadar_alkohol ?? 0,

        hs_code: summaryValues.hs_code ?? null,
        uraian: summaryValues.uraian ?? null,

        lampiran_1: summaryValues.lampiran_foto_1 ?? null,
        lampiran_2: summaryValues.lampiran_foto_2 ?? null,
      };

      if (idItem) {
        await updateItem(idItem, payload);
        message.success("Item berhasil diperbarui");
      } else {
        await createItem(payload);
        message.success("Item berhasil disimpan");
        form.resetFields();
        setFileListFoto1([]);
        setFileListFoto2([]);
      }

      setConfirmVisible(false);
      // Redirect menggunakan React Router
      history.push("/referensi/komoditas");
    } catch (err) {
      console.error(err);
      message.error("Gagal menyimpan data item");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // balik ke halaman sebelumnya
    history.goBack();
  };

  return (
    <Card
      loading={loadingPage}
      className="kt-portlet"
      bodyStyle={{ padding: "16px" }}
    >
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ marginBottom: 0 }}>
            {idItem ? "Edit Komoditas" : "Form Penambahan Komoditas"}
          </Title>
          <Text type="secondary">
            Lengkapi data komoditas sesuai dokumen kepabeanan.
          </Text>
        </Col>
      </Row>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        requiredMark="optional"
      >
        <Row gutter={[16, 0]}>
          {/* Kategori */}
          <Col xs={24} md={12}>
            <Form.Item
              label="Kategori"
              name="id_item_category"
              rules={[{ required: true, message: "Kategori wajib diisi" }]}
            >
              <Select
                placeholder="Pilih kategori"
                showSearch
                optionFilterProp="children"
                onChange={handleCategoryChange}
              >
                {categories.map((cat) => (
                  <Option
                    key={cat.id_item_category}
                    value={cat.id_item_category}
                  >
                    {cat.category}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* Kode Barang */}
          <Col xs={24} md={12}>
            <Form.Item
              label="Kode Barang"
              name="item_code"
              rules={[{ required: true, message: "Kode barang wajib diisi" }]}
            >
              <Input placeholder="Kode akan terisi otomatis, masih bisa diedit" />
            </Form.Item>
          </Col>

          {/* HS Code */}
          <Col xs={24} md={12}>
            <Form.Item
              label="HS Code"
              name="hs_code"
              rules={[
                { required: true, message: "HS Code wajib diisi" },
                {
                  pattern: /^[0-9]{1,8}$/,
                  message: "HS Code hanya angka, maksimal 8 digit",
                },
              ]}
            >
              <Input placeholder="Contoh: 17019910" maxLength={8} />
            </Form.Item>
          </Col>

          {/* Uraian */}
          <Col xs={24} md={12}>
            <Form.Item
              label="Uraian Barang"
              name="uraian"
              rules={[{ required: true, message: "Uraian wajib diisi" }]}
            >
              <Input.TextArea
                rows={3}
                placeholder="Golongan B (Kadar Etil Alkohol 5% - 20%)"
              />
            </Form.Item>
          </Col>

          {/* Isi Kemasan */}
          <Col xs={24} md={12}>
            <Form.Item
              label="Isi Kemasan"
              name="isi_kemasan"
              rules={[{ required: true, message: "Isi kemasan wajib diisi" }]}
            >
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                placeholder="Angka saja"
              />
            </Form.Item>
          </Col>

          {/* Isi Per Unit */}
          <Col xs={24} md={12}>
            <Form.Item
              label="Isi Per Unit"
              name="isi_per_unit"
              rules={[{ required: true, message: "Isi per unit wajib diisi" }]}
            >
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                placeholder="Angka saja"
              />
            </Form.Item>
          </Col>

          {/* Satuan Kemasan */}
          <Col xs={24} md={12}>
            <Form.Item
              label="Satuan Kemasan"
              name="satuan_kemasan"
              rules={[
                { required: true, message: "Satuan kemasan wajib diisi" },
                { max: 10, message: "Maksimal 10 karakter" },
              ]}
            >
              <Input placeholder="CT, CG, dll" maxLength={10} />
            </Form.Item>
          </Col>

          {/* Satuan Unit */}
          <Col xs={24} md={12}>
            <Form.Item
              label="Satuan Unit"
              name="satuan_unit"
              rules={[
                { required: true, message: "Satuan unit wajib diisi" },
                { max: 10, message: "Maksimal 10 karakter" },
              ]}
            >
              <Input placeholder="BO, PCS, PCE, dll" maxLength={10} />
            </Form.Item>
          </Col>

          {/* Merk */}
          <Col xs={24} md={12}>
            <Form.Item label="Merk" name="merk">
              <Input placeholder="Merk barang (jika ada)" />
            </Form.Item>
          </Col>

          {/* Kadar Alkohol (conditional) */}
          {!shouldHideAlcoholField && (
            <Col xs={24} md={12}>
              <Form.Item
                label="Kadar Alkohol"
                name="kadar_alkohol"
                rules={[
                  {
                    pattern: /^[0-9]*([.,][0-9]+)?$/,
                    message: "Hanya angka, boleh desimal",
                  },
                ]}
              >
                <Input placeholder="Contoh: 0, 40 atau 40.5" />
              </Form.Item>
            </Col>
          )}

          {/* Lampiran Foto 1 */}
          <Col xs={24} md={12}>
            <Form.Item label="Lampiran Foto 1" name="lampiran_foto_1">
              <Upload
                listType="picture"
                fileList={fileListFoto1}
                maxCount={1}
                customRequest={async (options) => {
                  const path = await handleCustomUpload(options);
                  const current = form.getFieldValue("lampiran_foto_1");
                  if (!current && path) {
                    form.setFieldsValue({ lampiran_foto_1: path });
                  }
                }}
                onChange={onChangeUploadFoto1}
              >
                <Button icon={<UploadOutlined />}>Upload Foto 1</Button>
              </Upload>
            </Form.Item>
          </Col>

          {/* Lampiran Foto 2 */}
          <Col xs={24} md={12}>
            <Form.Item label="Lampiran Foto 2" name="lampiran_foto_2">
              <Upload
                listType="picture"
                fileList={fileListFoto2}
                maxCount={1}
                customRequest={async (options) => {
                  const path = await handleCustomUpload(options);
                  const current = form.getFieldValue("lampiran_foto_2");
                  if (!current && path) {
                    form.setFieldsValue({ lampiran_foto_2: path });
                  }
                }}
                onChange={onChangeUploadFoto2}
              >
                <Button icon={<UploadOutlined />}>Upload Foto 2</Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        <Row justify="end" style={{ marginTop: 16 }}>
          <Space wrap>
            <Button
              danger
              onClick={handleCancel}
              style={{ minWidth: 120, fontWeight: 500 }}
            >
              BATAL
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={saving}
              style={{
                minWidth: 140,
                fontWeight: 600,
                background: "#1BC5BD",
                borderColor: "#1BC5BD",
              }}
            >
              SIMPAN
            </Button>
          </Space>
        </Row>

        <Row style={{ marginTop: 24 }}>
          <Col span={24}>
            <Text type="secondary">
              NB: Konfirmasi simpan dengan summary isian di modal. Jika kategori
              REL, Barang Promosi, atau Inventaris, field Kadar Alkohol
              disembunyikan dan otomatis 0.
            </Text>
          </Col>
        </Row>
      </Form>

      {/* MODAL KONFIRMASI */}
      <Modal
        open={confirmVisible}
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: "#FAAD14" }} />
            <span>Konfirmasi Simpan Komoditas</span>
          </Space>
        }
        onCancel={() => setConfirmVisible(false)}
        onOk={handleConfirmSave}
        okText="Simpan"
        cancelText="Batal"
        confirmLoading={saving}
        width={Math.min(720, window.innerWidth - 32)}
      >
        {summaryValues && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Kategori">
              {summaryValues.kategori_label}
            </Descriptions.Item>
            <Descriptions.Item label="Kode Barang">
              {summaryValues.item_code}
            </Descriptions.Item>
            <Descriptions.Item label="HS Code">
              {summaryValues.hs_code}
            </Descriptions.Item>
            <Descriptions.Item label="Uraian">
              {summaryValues.uraian}
            </Descriptions.Item>
            <Descriptions.Item label="Isi Kemasan">
              {summaryValues.isi_kemasan}
            </Descriptions.Item>
            <Descriptions.Item label="Satuan Kemasan">
              {summaryValues.satuan_kemasan}
            </Descriptions.Item>
            <Descriptions.Item label="Isi Per Unit">
              {summaryValues.isi_per_unit}
            </Descriptions.Item>
            <Descriptions.Item label="Satuan Unit">
              {summaryValues.satuan_unit}
            </Descriptions.Item>
            <Descriptions.Item label="Merk">
              {summaryValues.merk || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Kadar Alkohol">
              {shouldHideAlcoholField ? 0 : summaryValues.kadar_alkohol || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Lampiran Foto 1">
              {summaryValues.lampiran_foto_1 || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Lampiran Foto 2">
              {summaryValues.lampiran_foto_2 || "-"}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </Card>
  );
}

// OPTIONAL: export service kalau mau dipakai di file lain
export const ItemService = {
  fetchItemCategories,
  generateItemCode,
  createItem,
  updateItem,
  getItemDetail,
  deleteItem,
};
